import { Sparkles } from "lucide-react";
import { useEffect, useId, useMemo, useRef, useState } from "react";

import { Badge, Button } from "@/components/ui";
import type { WhatsNewItem } from "@/lib/types";
import { newestFirst, SNOOZE_DAYS, snoozeUntil, useMarkWhatsNew, useWhatsNew } from "@/lib/whats-new";

import { ItemLink } from "./ItemLink";

const LINK = "text-sm font-semibold text-brand-600 hover:underline";

/**
 * The header's "What's new": a button with a badge for what's new to this person and a panel under it. The panel is
 * a disclosure, not a dialog: no scrim, no focus trap, the page stays usable, Escape or a click elsewhere closes it.
 * If the feed can't load, or there is nothing for this person, nothing is drawn.
 */
export function WhatsNewMenu() {
  const feed = useWhatsNew();
  const mark = useMarkWhatsNew();
  const [open, setOpen] = useState(false);
  // What was new when the panel opened: opening marks it seen (the badge clears), but its labels stay while you read.
  const [freshAtOpen, setFreshAtOpen] = useState<ReadonlySet<string>>(new Set());
  const wrapper = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLElement>(null);
  const panelId = useId();

  const news = useMemo(
    () => newestFirst((feed.data?.items ?? []).filter((i) => i.kind === "news")),
    [feed.data],
  );
  const newCount = feed.data?.new_count ?? 0;

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setOpen(false);
      // Give focus back only if it was ours to give: never pull it out of something else the person moved to.
      const active = document.activeElement;
      if (!active || active === document.body || wrapper.current?.contains(active)) trigger.current?.focus();
    };
    const onMouseDown = (event: MouseEvent) => {
      if (!wrapper.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onMouseDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onMouseDown);
    };
  }, [open]);

  // Stay while open even when empty, so the button a person is using never vanishes under them.
  if (!open && news.length === 0) return null;

  const toggle = () => {
    if (open) {
      setOpen(false);
      return;
    }
    const fresh = news.filter((i) => i.is_new).map((i) => i.id);
    setFreshAtOpen(new Set(fresh));
    setOpen(true);
    if (fresh.length) mark.mutate({ ids: fresh, seen: true });
  };

  const act = (item: WhatsNewItem, change: { dismissed: true } | { snooze_until: string }) => {
    mark.mutate({ ids: [item.id], ...change });
    // The button that was just used is about to go: keep focus in the panel rather than drop it on the page.
    panel.current?.focus();
  };

  return (
    // Not positioned on small screens: there the panel hangs from the header and spans it (see AppShell).
    <div ref={wrapper} className="sm:relative print:hidden">
      <button
        ref={trigger}
        type="button"
        onClick={toggle}
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        aria-label={newCount > 0 ? `What's new, ${newCount} new` : "What's new"}
        className="relative flex size-[38px] items-center justify-center rounded-[10px] border border-border bg-surface text-fg-muted hover:text-fg"
      >
        <Sparkles className="size-[18px]" aria-hidden />
        {newCount > 0 && (
          <span
            aria-hidden
            className="absolute -top-1.5 -right-1.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-brand-600 px-1 text-[11px] leading-none font-semibold text-white"
          >
            {newCount > 9 ? "9+" : newCount}
          </span>
        )}
      </button>

      {open && (
        <section
          ref={panel}
          id={panelId}
          aria-label="What's new"
          tabIndex={-1}
          className="absolute inset-x-3 top-full z-30 -mt-1 max-h-[70vh] overflow-y-auto rounded-xl border border-border bg-surface shadow-lg outline-none sm:inset-x-auto sm:right-0 sm:mt-2 sm:w-96"
        >
          <h2 className="border-b border-border px-4 py-3 text-sm font-semibold text-fg">What&apos;s new</h2>
          {news.length === 0 ? (
            <p className="px-4 py-6 text-sm text-fg-muted">You&apos;re all caught up.</p>
          ) : (
            <ul className="divide-y divide-border">
              {news.map((item) => {
                const headingId = `${panelId}-${item.id}`;
                return (
                  <li key={item.id} aria-labelledby={headingId} className="flex flex-col gap-2 px-4 py-3">
                    <div className="flex items-start gap-2">
                      <h3 id={headingId} className="min-w-0 flex-1 text-sm font-semibold text-fg">
                        {item.title}
                      </h3>
                      {(item.is_new || freshAtOpen.has(item.id)) && <Badge tone="info">New</Badge>}
                    </div>
                    <p className="text-sm text-fg-muted">{item.body}</p>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                      {item.action && (
                        <ItemLink url={item.action.url} className={LINK} onNavigate={() => setOpen(false)}>
                          {item.action.label}
                        </ItemLink>
                      )}
                      {item.learn_more_url && (
                        <ItemLink url={item.learn_more_url} className={LINK}>
                          Learn more
                        </ItemLink>
                      )}
                      <span className="flex-1" />
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label={`Snooze for a week: ${item.title}`}
                        onClick={() => act(item, { snooze_until: snoozeUntil(SNOOZE_DAYS) })}
                      >
                        Snooze
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label={`Dismiss: ${item.title}`}
                        onClick={() => act(item, { dismissed: true })}
                      >
                        Dismiss
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      )}
    </div>
  );
}
